import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import dynamic from "next/dynamic";
import Footer from "./footer";
import Header from "./header";
import Image, { ImageLoaderProps } from "next/image";
import StoneityButton from "@/components/stoneity-button/stoneityButton";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useAppSelector } from "@/redux/app/hooks";
import { isTryDemoViewableRedux } from "@/redux/slices/user-context";
import { useTranslation } from "next-i18next";
import { Navigation, Autoplay } from "swiper";
import type { GetStaticProps, InferGetStaticPropsType } from "next";
import Link from "next/link";
import styles from "../styles/Home.module.css";
import "swiper/css";
// import "swiper/swiper-bundle.css";
import { IMAGE_DELIVERY, imageLoader } from "@/base/utils/dataHelper";
import { useRouter } from "next/router";
import ProductIcons from "./images/productIcons";
import SolutionProjects from "./images/solutionProjects";
import SolutionTasks from "./images/solutionTasks";
import SolutionProcess from "./images/solutionProcess";
import SolutionWorkflows from "./images/solutionWorkflows";
import SolutionMailBox from "./images/solutionMailBox";
import SolutionPortal from "./images/solutionPortal";
import SolutionIssueType from "./images/solutionIssueType";
import Productivity from "./images/productivity";
import Partnership from "./images/partnership";
import AutomationProductInfo from "./images/automationProductInfo";
import Speed from "./images/speed";
import Control from "./images/control";
import Flexibility from "./images/flexibility";
import Chatgpt from "./images/chatgpt";
import Close from "./images/close";

const Demo = dynamic(() => import("@/components/demo"), {
  ssr: false,
  loading: () => <div style={{ minHeight: 640 }} aria-hidden="true" />,
});

enum ProductFeaturesEnum {
  Project,
  Task,
  ProjectView,
  Form,
}

interface ProductCard {
  id: number;
  title: string;
  name: string;
  image: string;
  width: number;
  height: number;
  link: string;
  color: string;
  icon: JSX.Element;
  boolean?: boolean;
  bottom?: number;
}

interface Solution {
  id: number;
  title: string;
  icon: JSX.Element;
  image: string;
  width: number;
  height: number;
  responsiveImage: string;
  opacity: number;
}

interface ProductInfo {
  id: number;
  title: string;
  icon: JSX.Element;
  infos: ProductInfoDetail[];
}

interface ProductInfoDetail {
  title: string;
  description: string;
  icon: JSX.Element;
}
interface Company {
  id: number;
  title: string;
  src: string;
}

const productInfos: ProductInfo[] = [
  {
    id: 1,
    title: "Work Management",
    icon: <ProductIcons color="#FC584A" />,
    infos: [
      {
        title: "productivity",
        description: "productivityExplain",
        icon: <Productivity />,
      },
      {
        title: "partnership",
        description: "partnershipExplain",
        icon: <Partnership />,
      },
      {
        title: "automation",
        description: "automationExplain",
        icon: <AutomationProductInfo />,
      },
    ],
  },
  // {
  //   id: 2,
  //   title: "Innerbox",
  //   icon: <ProductIcons color="#304DDB" />,
  //   infos: [
  //     {
  //       title: "quickAnswers",
  //       description: "quickAnswersExplain",
  //       icon: <QuickAnswers />,
  //     },
  //     {
  //       title: "moreRequests",
  //       description: "moreRequestsExplain",
  //       icon: <MoreRequests />,
  //     },
  //     {
  //       title: "order",
  //       description: "orderExplain",
  //       icon: <Order />,
  //     },
  //   ],
  // },
  {
    id: 3,
    title: "Service Management",
    icon: <ProductIcons color="#478B5A" />,
    infos: [
      {
        title: "speed",
        description: "speedExplain",
        icon: <Speed />,
      },
      {
        title: "control",
        description: "controlExplain",
        icon: <Control />,
      },
      {
        title: "flexibility",
        description: "flexibilityExplain",
        icon: <Flexibility />,
      },
    ],
  },
];

type HomePageProps =
  InferGetStaticPropsType<typeof getStaticProps> & {
    setWidgetMenuOpen: (value: boolean) => void;
  };

const HomePage = ({ setWidgetMenuOpen, ...props }: HomePageProps) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const isTryDemoViewable = useAppSelector(isTryDemoViewableRedux);
  const swiperRef = useRef<SwiperType>();
  const isLanguage = router.locale?.toUpperCase().toString();
  const [activeSolution, setActiveSolution] = useState<number>(1);
  const [activeProductInfo, setActiveProductInfo] = useState<number>(1);
  const [isWideScreen, setIsWideScreen] = useState(false);
  const [homeInfo, setHomeInfo] = useState(true);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 1100);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const solutions: Solution[] = [
    {
      id: 1,
      title: "projects",
      icon: <SolutionProjects />,
      image: isLanguage === "TR" ? "619f8b64-840b-4d39-f3b8-d2e552091400" : "3040b03f-07c2-411a-bf14-89f6e2766300",
      responsiveImage: isLanguage === "TR" ? "dec40d70-8d46-47fe-0b8d-b36e426c5700" : "7c799bc2-74b1-4da8-3fee-f7cb06c9f500",
      width: isWideScreen ? 1077 : 1000,
      height: 735,
      opacity: 1
    },
    {
      id: 2,
      title: "tasks",
      icon: <SolutionTasks />,
      image: isLanguage === "TR" ? "80faa98a-a1e5-467b-5e95-7f812e4e9300" : "fff27678-190f-4654-04a9-cbfb46828600",
      responsiveImage: isLanguage === "TR" ? "b4f2bfa1-e05c-452c-5ccb-0841680c1100" : "d750bcc7-0f3a-493f-ae7c-380d84578b00",
      width: isWideScreen ? 1105 : 1000,
      height: 754,
      opacity: 1
    },
    {
      id: 3,
      title: "modulerWorkflows",
      icon: <SolutionWorkflows />,
      image: isLanguage === "TR" ? "b34b5efe-d18f-4217-14af-1eac86922800" : "c72adeef-97b9-4028-625f-0ec913dcd800",
      responsiveImage: isLanguage === "TR" ? "30ddd105-0916-4ff3-3ea5-aa61dd064100" : "703d8d48-d6c4-4234-df0b-46025d77ab00",
      width: isWideScreen ? 1175 : 1000,
      height: 551,
      opacity: 1
    },
    {
      id: 4,
      title: "sharedMailBox",
      icon: <SolutionMailBox />,
      image: isLanguage === "TR" ? "cc90e474-522c-43a9-7e22-aec939ff6900" : "095cec50-47ef-411a-ee25-991a9bf9dc00",
      responsiveImage: isLanguage === "TR" ? "23ec7ac2-c724-42c6-01cb-c2758a9e2c00" : "9884f01b-ce4f-4f1d-e2bd-a3c463a53600",
      width: isWideScreen ? 790 : 1000,
      height: 693,
      opacity: 1
    },
    {
      id: 5,
      title: "servicePortal",
      icon: <SolutionPortal />,
      image: isLanguage === "TR" ? "ac87a5ff-6fac-46b4-7336-acf6132aa500" : "65a684b2-c2eb-481e-d2f7-f1040e11bc00",
      responsiveImage: isLanguage === "TR" ? "316c01c7-66a6-42e4-822c-a269a5a63e00" : "6f6b6dbf-2a88-46c3-6942-396921cd7200",
      width: isWideScreen ? 1085 : 1000,
      height: 728,
      opacity: 1
    },
    {
      id: 6,
      title: "issueType",
      icon: <SolutionIssueType />,
      image: isLanguage === "TR" ? "168512e9-4a1d-4794-0822-b8549dadbf00" : "44207c39-b719-4e07-eb53-fe1b26be4a00",
      responsiveImage: isLanguage === "TR" ? "49d78516-2d56-4649-c3c3-1f60cfdda900" : "8ff2e16a-e64f-454c-a42d-123684ef4c00",
      width: isWideScreen ? 950 : 1000,
      height: 495,
      opacity: 1
    },
  ];

  const productCards: ProductCard[] = [
    {
      id: 1,
      title: "projectsTasks",
      name: "Work Management",
      image: isLanguage === "TR" ? "/467e3d3d-23b0-4c69-58a0-07c7be054600" : "/9e67da5f-eb0e-49a7-384e-e23ab63b7300",
      width: 300,
      height: 202,
      link: "/work-management",
      color: "linear-gradient(to right bottom, #269785, #2fa18e, #38aa98, #40b4a1, #48beab)",
      icon: <ProductIcons color="#FC584A" />,
    },
    {
      id: 2,
      title: "iTSupport",
      name: "Service Management",
      image: isLanguage === "TR" ? "/f332b971-c047-4fca-6f0a-ad054ebbac00" : "/40416858-739e-44a2-4af2-20bb2370f400",
      width: 300,
      height: 224,
      link: "/service-management",
      color: "linear-gradient(to right bottom, #538cf0, #6598f3, #76a3f5, #87aff7, #97baf9)",
      icon: <ProductIcons color="#478B5A" />,
    },
    {
      id: 3,
      title: "productDevelopment",
      name: "Work Management",
      image: isLanguage === "TR" ? "/c2e7fa48-9de4-4806-2e0a-110a5e4e0600" : "/de8f61cb-e251-464e-b84c-088077e13800",
      width: 300,
      height: 233,
      link: "/work-management",
      color: "linear-gradient(to right bottom, #5a8f5c, #659967, #6fa371, #7aad7c, #85b787)",
      icon: <ProductIcons color="#FC584A" />,
    },
    {
      id: 4,
      title: "customerSupport",
      name: "Service Management",
      image: isLanguage === "TR" ? "/51d14185-4975-46b3-be0e-19af3ebe6600" : "/2c1d94c4-7641-4552-11e5-9232a59cd300",
      width: 300,
      height: 230,
      link: "/service-management",
      color: "linear-gradient(to right bottom, #8a4ebd, #8e51c2, #9354c8, #9758cd, #9c5bd3)",
      icon: <ProductIcons color="#478B5A" />,
    },
    {
      id: 5,
      title: "processAutomations",
      name: "Work Management",
      image: isLanguage === "TR" ? "/036ac8c1-3869-478b-6b16-72a399dd8800" : "/0941f54e-f543-4d1a-5f4a-39872ea40300",
      width: 300,
      height: 209,
      link: "/work-management",
      color: "linear-gradient(to right bottom, #729eea, #7fa8ef, #8cb2f5, #99bcfa, #a6c6ff)",
      icon: <ProductIcons color="#FC584A" />,
    },
    {
      id: 6,
      title: "documentManagement",
      name: "Service Management",
      image: isLanguage === "TR" ? "/f396720a-fc86-4f72-149c-dbf91f208500" : "/76cbe6f3-bab8-461d-dc3f-6c87c51ca600",
      width: 300,
      height: 217,
      link: "/service-management",
      color: "linear-gradient(to right bottom, #8070d3, #8c7ddd, #998ae6, #a597f0, #b2a4fa)",
      icon: <ProductIcons color="#478B5A" />,
    },
  ];

  const companys: Company[] = [
    {
      id: 1,
      title: "Vodafone",
      // src: "a2b0d679-ec82-48c4-10fe-fd0c87a7a300"
      src: "7c1030e5-5801-4149-d796-6335b28c4700"
    },
    {
      id: 2,
      title: "PTT",
      // src: "8da10395-f724-454c-84e2-0f293e894000"
      src: "4e341b6b-bf21-4c56-a859-d6b6000a1600"
    },
    {
      id: 3,
      title: "Afad",
      // src: "3c415abb-f484-400b-9005-0094582f1400"
      src: "453080fb-2f06-41eb-5d8e-54cebb5a5200"
    },
    {
      id: 4,
      title: "Kardemir",
      // src: "0c01ac34-f459-401e-0a85-64c790775600"
      src: "e4415ddb-3e73-49ae-78bc-acb44db61b00"
    },
    {
      id: 5,
      title: "VBT",
      // src: "42b914a7-e61f-46d0-a095-b8c4e9c86e00"
      src: "6f69c072-61c2-4e55-5ee8-904c03d1b300"
    },
    {
      id: 6,
      title: "Artitel",
      // src: "7a5f0fc7-e19b-46f5-2759-9a66c6db5000"
      src: "007d9535-2d28-46da-6bc1-897ea9ffd800"
    },
  ]

  const handleMouseEnter = () => {
    if (swiperRef.current) {
      swiperRef.current.autoplay.pause();
    }
  };

  const handleMouseLeave = () => {
    if (swiperRef.current) {
      swiperRef.current.autoplay.start();
    }
  };

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.wrapperEl.style.transform = "translate3d(0px, 0, 0)";
    }
  }, []);

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath === '/' ? '' : currentPath}`
    : `${baseUrl}${currentPath === '/' ? '' : currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternateUrl = locale === 'tr'
    ? `${baseUrl}${currentPath === '/' ? '' : currentPath}`
    : `${baseUrl}/tr${currentPath === '/' ? '' : currentPath}`;

  return (
    <>
      <Head>
        <title>{t("seo_home_title")}</title>
        <meta name="description" content={t("seo_home_description")} />
        <meta name="keywords" content={locale === 'tr'
          ? "proje yönetimi, iş yönetimi, görev takibi, ekip yönetimi, servis yönetimi, ITSM, Stoneity, work management, service management"
          : "project management, work management, task tracking, team management, service management, ITSM, Stoneity"} />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t("seo_home_title")} />
        <meta property="og:description" content={t("seo_home_description")} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t("seo_home_title")} />
        <meta name="twitter:description" content={t("seo_home_description")} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={alternateUrl} />
        <link rel="alternate" hrefLang="x-default" href={baseUrl} />

        {/* Structured Data for Google Sitelinks */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Stoneity",
              "url": baseUrl,
              "logo": `${baseUrl}/stoneityLogo.png`,
              "description": t("schema_organization_description"),
              "sameAs": [
                "https://twitter.com/stoneity"
              ],
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Sales",
                "email": "info" + "@" + "stoneity" + "." + "com"
              }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "Stoneity",
              "url": baseUrl
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SiteNavigationElement",
              "name": t("schema_main_menu"),
              "url": baseUrl,
              "hasPart": [
                {
                  "@type": "SiteNavigationElement",
                  "name": t("schema_work_management_name"),
                  "url": `${baseUrl}/work-management`,
                  "description": t("schema_work_management_description")
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": t("schema_service_management_name"),
                  "url": `${baseUrl}/service-management`,
                  "description": t("schema_service_management_description")
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": t("features"),
                  "url": `${baseUrl}/work-management/features`,
                  "description": t("schema_features_description")
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": t("plans"),
                  "url": `${baseUrl}/work-management/plan`,
                  "description": t("schema_plans_description")
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": t("documents"),
                  "url": `${baseUrl}/work-management/documents`,
                  "description": t("schema_documents_description")
                },
                {
                  "@type": "SiteNavigationElement",
                  "name": t("schema_try_demo_name"),
                  "url": `${baseUrl}/try-demo`,
                  "description": t("schema_try_demo_description")
                }
              ]
            })
          }}
        />
      </Head>
      <Header />
      <main className={styles.mainFlex}>
        <div className={`${styles.homeInfoCardWrapper} ${homeInfo ? "" : styles.homeInfoCardWrapperDisactive}`}>
          <div className={styles.homeInfoCardTop}>
            <div className={styles.homeInfoCardTopLeft}>
              <div className={styles.homeInfoCardTopLeftTitle}>
                <Chatgpt />
                {t("stoneityAssistantTitle")}
              </div>
              <Link className={styles.startAssistantButtonLink} target="_blank" href={"https://chatgpt.com/g/g-693693d48c4c8191b21834053a70c4d2-stoneity-work-management-assistant"}>
                <div className={styles.startAssistantButton}>
                  {t("startAssistant")}
                </div>
              </Link>
            </div>
            <div className={styles.closeButtonWrapper} onClick={() => { setHomeInfo(false) }}>
              <Close />
            </div>
          </div>
          <div className={styles.homeInfoCardBottom}>
            {t("stoneityAssistantExplain")}
          </div>
        </div>
        <div className={`${styles.newHomeBannerWrapper} ${homeInfo ? "" : styles.homeBannerContentPadding}`}>
          <div className={styles.marqueeWrapper}>
            <div className={styles.marqueeTrack}>
              <div className={styles.marqueeGroup}>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#fc584a" }}>{t("itsm")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#478b5a" }}>{t("customerServiceManagement")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#03555e" }}>{t("esmEnterpriseServiceManagement")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#fc584a" }}>{t("projectAndStrategyManagement")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#006178" }}>{t("workflowAndOperations")}</div>
              </div>
              <div className={styles.marqueeGroup}>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#fc584a" }}>{t("itsm")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#478b5a" }}>{t("customerServiceManagement")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#03555e" }}>{t("esmEnterpriseServiceManagement")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#fc584a" }}>{t("projectAndStrategyManagement")}</div>
                <div className={styles.newHomeBannertRotaterButton} style={{ backgroundColor: "#006178" }}>{t("workflowAndOperations")}</div>
              </div>
            </div>
          </div>
          <div className={`${styles.newHomeBannerTextWrapper} ${isLanguage === "TR" ? "" : styles.newHomeBannerTextLanguageWidth}`}>
            {/* <div className={styles.newHomeBannerText}>{t("manageServiceProcesses")}</div>
            <div className={styles.newHomeBannerText}>{t("simplifyOperationsWithITIL")}</div> */}
            <div className={styles.newHomeBannerText}>{t("homeBannerNewText")}</div>
          </div>
          <div className={styles.newHomeBannerButtonWrapper}>
            <div className={styles.talkWithUsButtonWrapper}>
              <div className={styles.talkWithUsButtonImgWrapper}>
                <img src="/human-1.png" alt="" />
                <img src="/human-2.png" alt="" />
                <img src="/human-3.png" alt="" />
              </div>
              <StoneityButton
                buttonClass="talkWithUsButton"
                buttonText={t("talkWithUs")}
                buttonStyle="light"
                width={200}
                height={45}
                onClick={() => setWidgetMenuOpen(true)}
              />
            </div>
            {isTryDemoViewable && (
              <Link className={styles.link} href={"/try-demo"}>
                <StoneityButton
                  buttonText={t("tryItForFree")}
                  buttonStyle="dark"
                  width={200}
                  height={45}
                />
              </Link>
            )}
          </div>
          <Image
            className={styles.newHomeBannerImg}
            src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/90921103-4f6f-4f09-4a08-204eb3f60600/1629x294` : `${IMAGE_DELIVERY}/a057c108-a783-4ccb-de6d-e6722d394a00/1629x294`}
            alt={""}
            width={1629}
            height={294}
            sizes="(max-width: 1629px) 100vw, 1629px"
            quality={85}
            priority
          />
        </div>
        {/* ESKİ BANNER TASARIMI */}
        {/* <div className={styles.homeBannerWrapper}>
          <div className={`${styles.homeBannerContent} ${homeInfo ? "" : styles.homeBannerContentPadding}`}>
            <div className={styles.homeBannerSlogan}>
              <div className={styles.homeBannerText}>{t("withTitle")}</div>
              <div className={styles.homeBannerAnimationTextWrapper}>
                <ul className={styles.homeBannerAnimationTextContent}>
                  <li className={styles.homeBannerAnimationTextItem}>
                    {t("projectManagement")}
                  </li>
                  <li className={styles.homeBannerAnimationTextItem}>
                    {t("mailManagement")}
                  </li>
                  <li className={styles.homeBannerAnimationTextItem}>
                    {t("increaseProductivity")}
                  </li>
                  <li className={styles.homeBannerAnimationTextItem}>
                    {t("reduceCosts")}
                  </li>
                </ul>
              </div>
              <div className={styles.bannerContentText}>
                {t("homeBannerText")}
              </div>
              {isTryDemoViewable && (
                <Link className={styles.link} href={"/try-demo"}>
                  <StoneityButton
                    buttonText={t("tryItForFree")}
                    buttonStyle="dark"
                    width={200}
                    height={50}
                  />
                </Link>
              )}
            </div>
            <Image
              src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/e220cafc-6417-498e-5c0a-b5f88b269400/875x759` : `${IMAGE_DELIVERY}/e594d995-27e5-4d7e-734b-58b73a4c0200/875x759`}
              alt={""}
              className={styles.homeBannerImg}
              width={875}
              height={759}
              sizes="(max-width: 768px) 380px, (max-width: 1200px) 50vw, 700px"
              priority
            />
          </div>
        </div> */}
        <div className={styles.productCardsWrapper}>
          {/* <div className={styles.productCardsTitle}>
            {t("whatToManage")}
          </div> */}
          <div className={styles.productCards}>
            {!isMounted && (
              <div className={styles.productCardsSkeleton}>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={styles.productCardSkeleton} />
                ))}
              </div>
            )}
            {isMounted && (
              <Swiper
                modules={[Navigation]}
                spaceBetween={15}
                slidesPerView={"auto"}
                loop={true}
                // speed={3000}
                autoplay={{
                  delay: 1,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                navigation={{
                  prevEl: "." + styles.productCardSlideWrapper,
                  nextEl: "." + styles.productCardSlideWrapper2,
                }}
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={styles.productCardsSwiperWrapper}
              >
                {productCards.map((productCard, index) => {
                  return (
                    <SwiperSlide key={index} className={styles.productCardSlide}>
                      <Link
                        href={productCard.link}
                        className={styles.productCard}
                        style={{
                          // backgroundImage: productCard.color,
                          position: "relative",
                        }}
                      >
                        <div className={styles.productCardTop}>
                          <div className={styles.title}>{t(productCard.title)}</div>
                          <div className={styles.name}>
                            {productCard.icon}
                            {productCard.name}
                          </div>
                        </div>
                        <div className={styles.image} style={{ width: '100%', maxWidth: productCard.width, maxHeight: productCard.height, marginLeft: "15px" }}>
                          <Image
                            src={`${IMAGE_DELIVERY}${productCard.image}/${productCard.width}x${productCard.height}`}
                            alt={""}
                            width={productCard.width}
                            height={productCard.height}
                            style={{ width: "100%", height: "auto" }}
                          />
                        </div>
                      </Link>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
            <div className={styles.productCardSlideWrapper}>
              <div className={styles.productCardCustomLeftArrow}></div>
            </div>
            <div className={styles.productCardSlideWrapper2}>
              <div className={styles.productCardCustomRightArrow}></div>
            </div>
          </div>
        </div>
        <div className={styles.companySloganWrapper}>
          <div className={styles.companySloganSwiperWrapper}>
            <div className={styles.companySloganTitle}>{t("companySloganText")} <span>Stoneity</span></div>
            {isMounted && (
              <Swiper
                loop={true}
                modules={[Autoplay]}
                slidesPerView={5}
                breakpoints={{
                  300: { slidesPerView: 3 },
                  600: { slidesPerView: 4 },
                  745: { slidesPerView: 5 },
                }}
                speed={1500}
                effect="fade"
                fadeEffect={{
                  crossFade: true
                }}
                autoplay={{
                  delay: 1500,
                  disableOnInteraction: false
                }}
                style={{ width: "100%" }}
              >
                {companys.map((company, index) => {
                  return (
                    <SwiperSlide key={index}>
                      <Image
                        className={styles.companySloganImg}
                        src={`${IMAGE_DELIVERY}/${company.src}`}
                        alt=""
                        width={120}
                        height={80}
                        loader={(p: ImageLoaderProps) => imageLoader(p.src, 120, 80)}
                      />
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            )}
          </div>
        </div>
        <div className={styles.solutionsMainContentWrapper}>
          <div className={styles.solutionsWrapper}>
            <div className={styles.solutionsTopWrapper}>
              <div className={styles.title}>{t("solutions")}</div>
              <div className={styles.solutionTabs}>
                {isMounted && (
                  <Swiper
                    key={isWideScreen ? 'desktop' : 'mobile'}
                    allowTouchMove={!isWideScreen}
                    centeredSlides={true}
                    centeredSlidesBounds={true}
                    slidesPerView={3}
                    breakpoints={{
                      500: { slidesPerView: 3 },
                      700: { slidesPerView: 5 },
                    }}
                    spaceBetween={10}
                    className={styles.documentCardMySwiper}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    modules={[Navigation]}
                    navigation={{
                      prevEl: "." + styles.solutionsPrevSlideWrapper,
                      nextEl: "." + styles.solutionsNextSlideWrapper,
                    }}
                  >
                    {solutions.map((solution, index) => (
                      <SwiperSlide
                        className={activeSolution === solution.id ? styles.solutionTabActive : styles.solutionTab}
                        key={`${solution.id}-${index}`}
                        virtualIndex={index}
                        onClick={() => {
                          setActiveSolution(solution.id);
                          swiperRef.current?.slideToLoop(index, 500);
                        }}
                      >
                        <div className={styles.solutionTabIcon} style={{ background: activeSolution === solution.id ? "#6B9CB3" : "transparent", }}>
                          {solution.icon}
                        </div>
                        <div className={styles.solutionTabTitle}>
                          {t(solution.title)}
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
                <div className={styles.solutionsPrevSlideWrapper}>
                  <div className={styles.solutionsCustomLeftArrow}></div>
                </div>
                <div className={styles.solutionsNextSlideWrapper}>
                  <div className={styles.solutionsCustomRightArrow}></div>
                </div>
              </div>
            </div>
            <div className={styles.solutionImageWrapper}
              style={{
                height: (isMounted && isWideScreen)
                  ? `${solutions.find((s) => s.id === activeSolution)?.height}px`
                  : undefined,
              }}
            >
              <div className={styles.responsiveHeightBox}></div>
              {solutions.map((solution) => (
                <Image
                  key={solution.id}
                  className={styles.solutionImage}
                  src={`${IMAGE_DELIVERY}/${isWideScreen ? solution.image : solution.responsiveImage}/${isWideScreen ? solution.width : 1000}x${isWideScreen ? solution.height : 645}`}
                  alt={solution.title}
                  style={{
                    position: "absolute",
                    width: "100%",
                    maxWidth: isWideScreen ? `${solution.width}px` : "1000px",
                    height: "auto",
                    opacity: activeSolution === solution.id ? 1 : 0,
                    zIndex: activeSolution === solution.id ? 2 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                  width={isWideScreen ? solution.width : 1000}
                  height={isWideScreen ? solution.height : 645}
                  sizes="(max-width: 768px) 380px, (max-width: 1200px) 80vw, 1000px"
                />
              ))}
            </div>
          </div>
        </div>
        <Demo t={t} />
        <div className={styles.productsInfoWrapper}>
          <div className={styles.productTopInfo}>
            <div className={styles.title}>{t("impactInTheSector")}</div>
            <div className={styles.productTabs}>
              {productInfos.map((productInfo, index) => {
                return (
                  <div
                    key={index}
                    className={styles.productTab}
                    onClick={() => setActiveProductInfo(productInfo.id)}
                    style={{
                      borderBottom:
                        activeProductInfo === productInfo.id
                          ? "3px solid #000"
                          : "2px solid transparent",
                    }}
                  >
                    <div className={styles.productTabIcon}>
                      {productInfo.icon}
                    </div>
                    <div className={styles.productTabTitle}>
                      {productInfo.title}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={styles.productInfos}>
            {productInfos
              .find((productInfo) => productInfo.id === activeProductInfo)
              ?.infos.map((productInfoDetail, index) => {
                return (
                  <React.Fragment key={index}>
                    <div className={styles.productInfo}>
                      <div className={styles.productInfoTitle}>
                        {t(productInfoDetail.title)}
                      </div>
                      <div className={styles.productInfoContent}>
                        <div className={styles.productInfoIcon}>
                          {productInfoDetail.icon}
                        </div>
                        <div className={styles.productInfoDescription}>
                          {t(productInfoDetail.description)}
                        </div>
                      </div>
                    </div>
                    {index < productInfos.length - 0 && (
                      <div className={styles.productInfoLine}></div>
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </main >
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || "en", ["common"])),
  },
});

export default HomePage;
